import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { styled, Typography } from '@mui/material'
import { format } from 'date-fns'
import {
  Communications,
  CommunicationsUtils,
  PhoneUtils,
} from '@pbt/pbt-ui-components'

import { getContactMethod } from '../../../store/duck/constants'
import {
  getCurrentAppointment,
  sendNewAppointmentConfirmationEmail,
  updateCurrentAppointment,
} from '../../../store/duck/schedules'
import { Appointment } from '../../../types/entities/appointments'
// @ts-ignore
import { useNavigateWithQueryString } from '../../../utils'
import KioskSelectableButton from '../../buttons/KioskSelectableButton'
import KioskScreen from '../KioskScreen'

const StyledRoot = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
}))

const StyledDate = styled(Typography)(({ theme }) => ({
  color: theme.colors.tabLabel,
  fontSize: '1.6rem',
  fontWeight: 400,
  marginBottom: theme.spacing(12),
  textAlign: 'center',
}))

const StyledQuestion = styled(Typography)(({ theme }) => ({
  color: theme.colors.primaryText,
  fontSize: '1.6rem',
  fontWeight: 500,
  marginBottom: theme.spacing(8),
  textAlign: 'center',
}))

const ButtonsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  justifyContent: 'center',
}))

const StyledSelectableButton = styled(KioskSelectableButton)<{
  isSelected?: boolean
}>(({ theme, isSelected }) => ({
  width: 176,
  height: 56,
  borderRadius: 28,
  border: theme.constants.inactiveBorder,
  color: theme.colors.tabLabel,
  fontWeight: 500,
  fontSize: '1.6rem',
  textTransform: 'none',
  padding: 0,
  ...(isSelected && {
    color: theme.colors.primaryText,
    backgroundColor: theme.colors.selectedOption,
    '&:hover': {
      color: theme.colors.primaryText,
      backgroundColor: theme.colors.selectedOption,
    },
  }),
}))

const ContactConfirmationScreen = () => {
  const dispatch = useDispatch()
  const navigateWithQueryString = useNavigateWithQueryString()
  const { t } = useTranslation('Common')

  const appointment = useSelector(getCurrentAppointment) as Appointment | null
  const contactMethods = useSelector(getContactMethod)

  const preferredContactMethod = contactMethods.find(
    (method) => method.id === appointment?.client?.preferredContactMethodId,
  )
  const isEmailPreferred = preferredContactMethod?.name
    ?.toLowerCase()
    .includes('email')

  const [selected, setSelected] = useState<'yes' | 'no' | null>(
    (appointment as any)?.contactConfirmationSelection || null,
  )

  const maskPhoneNumber = (phone: string) => {
    const formatted = PhoneUtils.formatPhoneNumber(phone)
    const digits = formatted.replace(/\D/g, '')
    const maskedDigits =
      digits.slice(0, -4).replace(/\d/g, 'X') + digits.slice(-4)
    const maskedPhone = formatted
    let digitIndex = 0
    return maskedPhone.replace(/\d/g, () => maskedDigits[digitIndex++] || '')
  }

  const contactInfo = isEmailPreferred
    ? appointment?.client?.email
    : maskPhoneNumber(appointment?.client?.mobilePhone || '')

  const handleYesClick = () => {
    setSelected('yes')
    if (appointment?.patient) {
      dispatch(
        updateCurrentAppointment({
          ...appointment,
          contactConfirmationSelection: 'yes',
          patient: appointment.patient,
        }),
      )
    }
  }
  const handleNoClick = () => {
    setSelected('no')
    if (appointment?.patient) {
      dispatch(
        updateCurrentAppointment({
          ...appointment,
          contactConfirmationSelection: 'no',
          patient: appointment.patient,
        }),
      )
    }
  }

  const handleNext = () => {
    if (selected === 'yes') {
      if (!appointment || !appointment.client) {
        navigateWithQueryString({ url: '/confirmation-with-source' })
        return
      }
      const { client } = appointment
      const to = isEmailPreferred ? client.email : client.mobilePhone
      const recipients = CommunicationsUtils.formRecipients(
        { client },
        to,
        isEmailPreferred
          ? Communications.MessageTransport.EMAIL
          : Communications.MessageTransport.SMS,
      )
      const message: any = {
        recipients,
        transport: isEmailPreferred
          ? Communications.MessageTransport.EMAIL
          : Communications.MessageTransport.SMS,
      }
      dispatch(sendNewAppointmentConfirmationEmail(appointment.id, message))
      navigateWithQueryString({ url: '/confirmation-with-source' })
    } else if (selected === 'no') {
      navigateWithQueryString({ url: '/how-did-you-hear' })
    }
  }

  const handleBack = () => {
    navigateWithQueryString({ url: '/calendar' })
  }

  const appointmentDate = appointment?.scheduledStartDatetime
    ? format(
        new Date(appointment.scheduledStartDatetime),
        "EEEE, MMMM do 'at' h:mmaaa 'PT'",
      )
    : ''

  return (
    <KioskScreen
      hideBackButton={false}
      justifyContent="flex-start"
      proceedButtonDisabled={selected === null}
      proceedButtonLabel={t('Common:NEXT_ACTION')}
      title={t('Common:YOUR_APPOINTMENT_IS_SCHEDULED_WITH_EXCLAMATION')}
      onBack={handleBack}
      onProceed={handleNext}
    >
      <StyledRoot>
        <StyledDate>{appointmentDate}</StyledDate>
        <StyledQuestion>
          {`${t(
            'Common:WOULD_YOU_LIKE_TO_SEND_CONFIRMATION_TO',
          )} ${contactInfo}?`}
        </StyledQuestion>
        <ButtonsContainer>
          <StyledSelectableButton
            isSelected={selected === 'yes'}
            onClick={handleYesClick}
          >
            {t('Common:YES')}
          </StyledSelectableButton>
          <StyledSelectableButton
            isSelected={selected === 'no'}
            onClick={handleNoClick}
          >
            {t('Common:NO')}
          </StyledSelectableButton>
        </ButtonsContainer>
      </StyledRoot>
    </KioskScreen>
  )
}

export default ContactConfirmationScreen
